// Portal access for this matter, from the office side: whether the client
// can sign in, with which credential, and the invitation that turns the
// case into an account. Real matters only - a fixture matter has no Mongo
// id and no access row (the DocWall rule).

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  enableClientAccess,
  fetchClientAccess,
  inviteClient,
  setClientAccessEnabled,
} from "../../api/clientAccess";
import { useCurrentUser } from "../auth/CurrentUserContext";
import { usePortalPolicy } from "../../hooks/usePortalPolicy";
import { isAdminRole } from "../../lib/roles";
import { Skeleton, SkeletonGroup } from "../chrome/Skeleton";
import { fmtDate } from "../../lib/dates";

interface Props {
  /** Mongo _id of the Case; absent for fixture matters. */
  caseId?: string;
}

export function ClientAccessCard({ caseId }: Props) {
  const queryClient = useQueryClient();
  const isAdmin = isAdminRole(useCurrentUser()?.role);
  // The firm's switch decides which doors the card may name; read only where
  // the card itself will show (a real matter, an admin).
  const caseIdDoor = usePortalPolicy(isAdmin && !!caseId).data?.caseIdLoginEnabled !== false;
  const [dob, setDob] = useState("");
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["client-access", caseId ?? ""],
    queryFn: () => fetchClientAccess(caseId as string),
    enabled: !!caseId && isAdmin,
    staleTime: 30_000,
  });

  const settle = () => queryClient.invalidateQueries({ queryKey: ["client-access", caseId ?? ""] });

  const enableMut = useMutation({
    mutationFn: (value: string) => enableClientAccess(caseId as string, value),
    onSuccess: async () => {
      setDob("");
      setError(null);
      setNote("Access is on. Send the invitation when the client is ready.");
      await settle();
    },
    onError: (err) => {
      setNote(null);
      setError(err instanceof Error ? err.message : "Could not turn access on.");
    },
  });

  const toggleMut = useMutation({
    mutationFn: (enabled: boolean) => setClientAccessEnabled(caseId as string, enabled),
    onSuccess: async (_res, enabled) => {
      setError(null);
      setNote(enabled ? "Access is on again." : "Access is off. Signing in stops immediately.");
      await settle();
    },
    onError: (err) => {
      setNote(null);
      setError(err instanceof Error ? err.message : "Could not change access.");
    },
  });

  const inviteMut = useMutation({
    mutationFn: () => inviteClient(caseId as string),
    onSuccess: async ({ to, queued }) => {
      setError(null);
      // A queued message is being retried by the mailer - "sent" would
      // overstate what has happened so far.
      setNote(
        queued
          ? `The message to ${to} hit a sending problem - mail is retrying it. Check the email log in a few minutes.`
          : access?.passwordSetAt
            ? `Reset link sent to ${to}. It works once, and their current password stops working when they use it.`
            : `Invitation sent to ${to}. The link works once and expires after a day.`,
      );
      await settle();
    },
    onError: (err) => {
      setNote(null);
      setError(err instanceof Error ? err.message : "Could not send the invitation.");
    },
  });

  if (!caseId || !isAdmin) return null;

  const access = data?.access ?? null;
  const inviteEmail = data?.inviteEmail ?? null;

  return (
    <section className="cc-card" style={{ marginTop: 14 }}>
      <div className="cc-card-head">
        <div>
          <div className="cc-card-title">Client portal</div>
          <div className="cc-card-sub">
            {access?.accessEnabled
              ? caseIdDoor || access.passwordSetAt
                ? "This client can sign in"
                : "This client can sign in once their account is set up"
              : access
                ? "Access is off"
                : "No access yet"}
          </div>
        </div>
        {access?.accessEnabled && (
          <div className="actions">
            <button
              type="button"
              className="cc-link-btn"
              disabled={inviteMut.isPending || !inviteEmail}
              onClick={() => inviteMut.mutate()}
            >
              {inviteMut.isPending
                ? "Sending..."
                : access.passwordSetAt
                  ? "Send a password reset"
                  : access.invitationSentAt
                    ? "Send a new invitation"
                    : "Send invitation"}
            </button>
            <button
              type="button"
              className="cc-link-btn"
              disabled={toggleMut.isPending}
              onClick={() => toggleMut.mutate(false)}
            >
              Turn off
            </button>
          </div>
        )}
      </div>

      {note && <div className="cc-card-note">{note}</div>}
      {error && (
        <div role="alert" className="cc-card-note bad">
          {error}
        </div>
      )}

      {isLoading && (
        <div style={{ padding: "10px 18px" }}>
          <SkeletonGroup label="Loading portal access">
            <Skeleton width="60%" />
            <Skeleton width="40%" />
          </SkeletonGroup>
        </div>
      )}

      {isError && (
        <div style={{ padding: "10px 18px", fontSize: 12.5, color: "var(--muted)" }}>
          We could not read the access for this matter.
        </div>
      )}

      {data && !access && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            alignItems: "flex-end",
            padding: "10px 18px",
          }}
        >
          <div style={{ width: "100%", fontSize: 12.5, color: "var(--muted)" }}>
            {caseIdDoor
              ? "Turning access on lets the client sign in with their case ID and this date of birth. The invitation, sent after, gives them an email and password as well."
              : "Turning access on records this date of birth. With the case ID door closed in Settings, the client signs in with the email and password the invitation sets up."}
          </div>
          <label className="cc-form-field">
            <span className="cc-form-label">Client date of birth</span>
            <input
              className="cc-field"
              type="date"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
            />
          </label>
          <button
            type="button"
            className="cc-link-btn"
            disabled={!dob || enableMut.isPending}
            onClick={() => enableMut.mutate(dob)}
          >
            {enableMut.isPending ? "Turning on..." : "Turn access on"}
          </button>
        </div>
      )}

      {access && (
        <div style={{ padding: "10px 18px", fontSize: 13, lineHeight: 1.7 }}>
          {/* Both doors while the firm keeps the case ID one open: a
              password does not close it (clientDobHash survives it), only
              the Settings switch does, and the card names no door that is
              shut. */}
          <div>
            {access.accessEnabled ? "Signs in" : "When access is back on, signs in"}
            {!caseIdDoor && !access.passwordSetAt ? (
              <>
                {" "}
                once the invitation sets up their account: the case ID door is closed in Settings
              </>
            ) : (
              <>
                {" "}
                with{" "}
                {access.passwordSetAt && (
                  <>
                    {access.email ?? "their email address"} and a password they chose
                    {caseIdDoor && ", and still with "}
                  </>
                )}
                {caseIdDoor && (
                  <>
                    the case ID <span className="tnum-mono">{access.clientEId}</span> and their date
                    of birth
                  </>
                )}
              </>
            )}
          </div>
          <div style={{ color: "var(--muted)" }}>
            {access.invitationSentAt
              ? `Invitation sent ${fmtDate(access.invitationSentAt)}${
                  access.passwordSetAt
                    ? `, account set up ${fmtDate(access.passwordSetAt)}`
                    : ", not used yet"
                }`
              : "No invitation sent yet"}
          </div>
          <div style={{ color: "var(--muted)" }}>
            {/* Per matter, not per person: a client with two matters has
                two rows, and signing in touches only the one they used. */}
            {access.lastLoginAt
              ? `Last signed in to this matter ${fmtDate(access.lastLoginAt)} (${access.loginCount} in total)`
              : "Has never signed in to this matter"}
          </div>
          {access.accessEnabled && !inviteEmail && (
            <div style={{ color: "var(--muted)" }}>
              No email address on this client, so there is nobody to invite. Add one to the file
              first.
            </div>
          )}
          {access.accessEnabled && inviteEmail && (
            <div style={{ color: "var(--muted)" }}>
              {access.passwordSetAt ? "A reset link goes to " : "An invitation goes to "}
              {inviteEmail}
              {access.email && access.email !== inviteEmail
                ? ` - and their sign-in address becomes that one, in place of ${access.email}`
                : ""}
            </div>
          )}
          {!access.accessEnabled && (
            <button
              type="button"
              className="cc-link-btn"
              disabled={toggleMut.isPending}
              onClick={() => toggleMut.mutate(true)}
            >
              Turn access back on
            </button>
          )}
        </div>
      )}
    </section>
  );
}
